import { isKnownLanguage, RELEASED_LANGUAGES, SOURCE_LANGUAGE, type LanguageCode } from './languages'

/** The released languages a person may read: the server's list (English alone when it names none) that this build also releases. */
export function releasedFor(identity: { releasedLanguages?: readonly string[] } | null): readonly LanguageCode[] {
  if (!identity) return RELEASED_LANGUAGES
  const server = identity.releasedLanguages ?? [SOURCE_LANGUAGE]
  return RELEASED_LANGUAGES.filter((code) => code === SOURCE_LANGUAGE || server.includes(code))
}

/** Unreleased languages this reviewer may read (the server's draftLanguages), known to this build. */
export function draftsFor(identity: { draftLanguages?: readonly string[] } | null): readonly LanguageCode[] {
  return (identity?.draftLanguages ?? []).filter((code) => isKnownLanguage(code) && !RELEASED_LANGUAGES.includes(code))
}

/** What the language menu offers: the released languages, then a reviewer's drafts. */
export function offeredFor(identity: { releasedLanguages?: readonly string[]; draftLanguages?: readonly string[] } | null): readonly LanguageCode[] {
  return [...releasedFor(identity), ...draftsFor(identity)]
}

/** The first of the candidates that is released, matching a browser tag by its base ("ru-RU" → ru); English otherwise. */
export function firstReleased(candidates: readonly (string | null | undefined)[], released: readonly LanguageCode[]): LanguageCode {
  for (const tag of candidates) {
    if (!tag) continue
    const lower = tag.toLowerCase()
    const found = released.find((code) => code.toLowerCase() === lower) ?? released.find((code) => code.toLowerCase() === lower.split('-')[0])
    if (found) return found
  }
  return SOURCE_LANGUAGE
}

/**
 * The staff screens' language: the person's own choice (released, or a reviewer's
 * draft), else the browser's first released language, else English. Before
 * sign-in, the sign-in screen's guess.
 */
export function staffLanguageOf(
  identity: { staff: { language?: string | null }; releasedLanguages?: readonly string[]; draftLanguages?: readonly string[] } | null,
  beforeSignIn: LanguageCode,
  browser: readonly string[],
): LanguageCode {
  if (!identity) return firstReleased([beforeSignIn], RELEASED_LANGUAGES)
  // A draft only by the reviewer's own choice, never from the browser.
  const own = identity.staff.language
  if (own && draftsFor(identity).includes(own)) return own
  return firstReleased([own, ...browser], releasedFor(identity))
}
